import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import { format } from "date-fns";
import {
  Box,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ToggleButton,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CropFreeIcon from "@mui/icons-material/CropFree";
import { useAppDispatch, useAppSelector } from "store";
import { InspectionDialog } from "./inspection/InspectionDialog";
import {
  selectSelectedJobVisit,
  setSelectedInspection,
  updateInspection,
} from "store/slices/jobs.store";
import StyledTextField from "components/StyledTextField";
import { Inspection } from "models/inspection";
import { StyledToggleButtonGroup } from "components/StyledToggleButtonGroup";
import { FilterAppBar } from "components/FilterAppBar";
import { BarcodeScannerDialog } from "components/barcodeScan";
import { scanditLicense } from "env";
import { InspectionFilterDialog } from "./inspection/InspectionFilterDialog";
import { filterInspectionsList } from "helpers/filterInspectionsList";
import { defaultInspectionsFilter, InspectionFilters } from "models/InspectionsFilter";
import { FormattedMessage, useIntl } from "react-intl";
import { TabProps } from "pages/jobs/jobDetails/visit/TabProps";
import { DeviceTestStatusEnum } from "./inspection/DeviceTestStatusEnum";
import { AddDeviceDialog } from "pages/jobs/dialogs/AddDeviceDialog";
import { acknowledgeNewDevice, selectNewDeviceIds } from "store/slices/deviceStatus.store";

export const InspectionsTab: FC<TabProps> = (props) => {
  const { setFab } = props;
  const dispatch = useAppDispatch();
  const hasBarcodeScanner = !!scanditLicense();
  const [openInspectionDialog, setOpenInspectionDialog] = useState(false);
  const [openInspectionFilterDialog, setOpenInspectionFilterDialog] = useState(false);
  const [openAddDeviceDialog, setOpenAddDeviceDialog] = useState(false);
  const [openScanner, setOpenScanner] = useState(false);
  const [search, setSearch] = useState<string>("");
  const { inspections } = useAppSelector(selectSelectedJobVisit);
  const [filter, setFilter] = useState<InspectionFilters>(defaultInspectionsFilter);
  const intl = useIntl();
  const filteredInspectionsList = filterInspectionsList(inspections, filter, search);
  const iconStyle = { marginLeft: 1 };
  const deviceDialogLoading = false;
  const newDeviceIds = useAppSelector(selectNewDeviceIds);

  useEffect(() => {
    const fab = undefined;
    setFab(fab);
  }, [setFab]);

  return (
    <Container>
      <FilterAppBar
        onClick={setOpenInspectionFilterDialog}
        foundCount={filteredInspectionsList.length}
        marginTop="104px"
      />
      <Grid
        item
        container
        alignItems="start"
        justifyContent="space-between"
        wrap="nowrap"
        sx={{ paddingTop: "12px" }}
      >
        <Grid item width="100%">
          <StyledTextField
            fullWidth
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            variant="outlined"
            placeholder={intl.formatMessage({ id: "inspection.search" })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <ClearIcon />
                </InputAdornment>
              ),
            }}
            data-testid="InspectionTab-Filter"
          />
        </Grid>
        {hasBarcodeScanner && (
          <Grid item>
            <IconButton onClick={() => setOpenScanner(true)} size="large" sx={{ pr: 0, ml: 1 }}>
              <CropFreeIcon fontSize="large" />
            </IconButton>
          </Grid>
        )}
      </Grid>

      <List>
        {filteredInspectionsList.map((inspection: Inspection) => {
          const isFlagged = newDeviceIds.includes(inspection.specification.id.toString());

          return (
            <div key={inspection.specification.id}>
              <ListItem
                key={inspection.specification.id}
                disablePadding
                onClick={() => {
                  dispatch(updateInspection({ inspection }));
                  dispatch(setSelectedInspection({ inspection }));
                  setOpenInspectionDialog(true);
                }}
                disableGutters
                secondaryAction={
                  <IconButton sx={{ padding: 0 }}>
                    {isFlagged && (
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          bgcolor: "blue", 
                          borderRadius: "50%",
                          cursor: "pointer",
                          marginLeft: 2,
                          alignSelf: "center",
                          transition: "transform 0.2s, box-shadow 0.2s",
                          "&:hover": {
                            transform: "scale(1.2)",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
                          },
                          "&:active": {
                            transform: "scale(0.95)",
                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                          },
                        }}
                        data-testid="InspectionTab-FlaggedIcon"
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(acknowledgeNewDevice(inspection.specification.id.toString()));
                        }}
                      />
                    )}
                    <KeyboardArrowRightIcon />
                  </IconButton>
                }
                data-testid={`InspectionTab-ListItem}`}
              >
                <ListItemButton disableGutters>
                  <Grid>
                    <Grid>
                      <Typography variant="body1" fontWeight={700}>
                        {`${inspection.specification.description} (${inspection.specification.partNumber})`}
                      </Typography>
                    </Grid>
                    <Grid>
                      <Typography variant="body1">{inspection.specification.location}</Typography>
                    </Grid>
                    <Grid>
                      <Typography variant="body1">
                        {inspection.specification.subLocation}
                      </Typography>
                    </Grid>

                    <Grid>
                    <Typography variant="body2">
                      <span style={{ fontWeight: "bold" }}>
                        <FormattedMessage id="inspection.lastService" defaultMessage="Last Service" />:
                      </span>{" "}
                      <span
                        style={{
                          fontWeight: "bold",
                          color:
                            inspection.specification.resultOfLastService?.toLowerCase() === "pass"
                              ? "green"
                              : inspection.specification.resultOfLastService?.toLowerCase() === "fail"
                              ? "red"
                              : inspection.specification.resultOfLastService?.toLowerCase() === "not tested"
                              ? "grey"
                              : "inherit",
                        }}
                      >
                        {inspection.specification.resultOfLastService}
                      </span>
                      {inspection.specification.resultOfLastService?.toLowerCase() !== "not tested" && 
                        ` on ${
                          inspection.specification.lastServiceDate
                            ? format(new Date(inspection.specification.lastServiceDate), "dd/MM/yyyy")
                            : "N/A"
                        }`}
                    </Typography>
                    </Grid>
                  </Grid>
                </ListItemButton>
              </ListItem>
              <StyledToggleButtonGroup
                value={inspection.testResult}
                onChange={(_, value) => {
                  dispatch(updateInspection({ inspection: { ...inspection, testResult: value } }));
                }}
                exclusive
                fullWidth
                size="small"
                aria-describedby="bool-type-error-text"
                data-testid="InspectionTestResultToggle"
              >
                <ToggleButton value={DeviceTestStatusEnum.Pass} color="success">
                  <FormattedMessage id="inspection.pass" />
                  <CheckIcon sx={iconStyle} />
                </ToggleButton>
                <ToggleButton value={DeviceTestStatusEnum.Fail} color="error">
                  <FormattedMessage id="inspection.fail" />
                  <CloseIcon sx={iconStyle} />
                </ToggleButton>
                <ToggleButton value={DeviceTestStatusEnum.NotTested}>
                  <FormattedMessage id="inspection.notTested" />
                </ToggleButton>
              </StyledToggleButtonGroup>
            </div>
          );
        })}
      </List>
      <Box
        sx={{
          position: "fixed",
          bottom: 80,
          right: 16,
          backgroundColor: "#1976d2",
          color: "white",
          borderRadius: "50%",
          width: 56,
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: 3,
          zIndex: 1000,
          cursor: "pointer",
          "&:hover": { backgroundColor: "#1565c0" },
        }}
        onClick={() => {
          setOpenAddDeviceDialog(true);
        }}
      >
        <NoteAddIcon sx={{ color: "white", fontSize: 32 }} />
      </Box>
      <AddDeviceDialog 
        open={openAddDeviceDialog}
        handleClose={() => {
          setOpenAddDeviceDialog(false);
        }}
        loading={deviceDialogLoading}
      />
      <InspectionDialog
        open={openInspectionDialog}
        handleClose={() => {
          setOpenInspectionDialog(false);
          dispatch(setSelectedInspection({ inspection: null }));
        }}
      />
      {hasBarcodeScanner && (
        <BarcodeScannerDialog
          open={openScanner}
          setOpen={(open: boolean) => setOpenScanner(open)}
          submitValue={(value: string) => {
            setSearch(value);
            setOpenScanner(false);
          }}
        />
      )}
      <InspectionFilterDialog
        open={openInspectionFilterDialog}
        handleClose={() => {
          setOpenInspectionFilterDialog(false);
        }}
        filter={filter}
        setFilter={setFilter}
      />
    </Container>
  );
};
